import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Plus,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader,
  Eye,
  EyeOff,
  Star,
  Save
} from 'lucide-react'
import Button from '../ui/Button'
import Input from '../ui/Input'
import { useTMSProjects } from '../../hooks/useTMSProjects'

const ProjectEditor = ({ project, isOpen, onClose, onSuccess, isDefault }) => {
  const { t } = useTranslation()

  // Use the TMS projects hook
  const {
    updateProject,
    setDefaultProject,
    testConnection,
    isUpdatingProject,
    isSettingDefault,
    isTestingConnection
  } = useTMSProjects()

  const [formData, setFormData] = useState({
    name: '',
    api_key: '',
    default_tags: [],
    default_overwrite_translations: true,
    default_flag_for_review: false,
    create_missing_locales: true,
    active: true,
    verified: false
  })

  const [newTag, setNewTag] = useState('')
  const [errors, setErrors] = useState({})
  const [connectionStatus, setConnectionStatus] = useState(null)
  const [showApiKey, setShowApiKey] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Initialize form data when project changes
  useEffect(() => {
    if (project) {
      setFormData({
        name: project.name || '',
        api_key: project.api_key || '',
        default_tags: Array.isArray(project.default_tags) ? [...project.default_tags] : [],
        default_overwrite_translations: project.default_overwrite_translations ?? true,
        default_flag_for_review: project.default_flag_for_review ?? false,
        create_missing_locales: project.create_missing_locales ?? true,
        active: project.active ?? true,
        verified: project.verified ?? false
      })
      setConnectionStatus(project.verified ? 'success' : null)
      setHasChanges(false)
    }
  }, [project])


  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm(t('tms.unsavedChangesWarning'))) {
        onClose()
      }
    } else {
      onClose()
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value }

      // Check if there are changes
      const originalValue = project[field]
      const isChanged = Array.isArray(value)
        ? JSON.stringify(value) !== JSON.stringify(originalValue)
        : value !== originalValue

      setHasChanges(isChanged || Object.keys(newData).some(key => {
        if (key === field) return false // Skip the field we just changed
        const original = project[key]
        const current = newData[key]
        return Array.isArray(current)
          ? JSON.stringify(current) !== JSON.stringify(original)
          : current !== original
      }))

      return newData
    })

    // Clear connection status when API key changes
    if (field === 'api_key') {
      setConnectionStatus(null)
    }

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }))
    }
  }

  const handleAddTag = () => {
    if (newTag.trim() && !formData.default_tags.includes(newTag.trim())) {
      const newTags = [...formData.default_tags, newTag.trim()]
      handleInputChange('default_tags', newTags)
      setNewTag('')
    }
  }

  const handleRemoveTag = (tagToRemove) => {
    const newTags = formData.default_tags.filter(tag => tag !== tagToRemove)
    handleInputChange('default_tags', newTags)
  }

  const handleTestConnection = async () => {
    if (!project.project_id || !formData.api_key) {
      setErrors({
        connection: t('tms.projectIdAndApiKeyRequired')
      })
      return
    }

    setConnectionStatus('testing')
    try {
      const data = await testConnection({
        projectId: project.project_id,
        apiKey: formData.api_key
      })

      // Handle response based on success field, not just HTTP status
      if (data.success) {
        setConnectionStatus('success')
        setErrors({})
        // Mark as having changes since verification status changed
        setHasChanges(true)
        // Update verified status in form data so it gets saved to database
        handleInputChange('verified', true)
        // Optionally update form with verified project name
        if (data.project_name && !formData.name) {
          handleInputChange('name', data.project_name)
        }
      } else {
        // Backend returned 200 but with success: false
        setConnectionStatus('error')
        setErrors({
          connection: data.error_message || t('tms.connectionTestFailed')
        })
      }
    } catch (error) {
      setConnectionStatus('error')
      setErrors({
        connection: error.response?.data?.detail || t('tms.connectionTestFailed')
      })
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    console.log('Submitting project update:', {
      projectId: project.project_id,
      updateData: formData
    })

    // Validation
    const newErrors = {}
    if (!formData.name.trim()) newErrors.name = t('validation.required')
    if (!formData.api_key.trim()) newErrors.api_key = t('validation.required')

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    updateProject({
      projectId: project.project_id,
      updateData: formData
    })
    .then((data) => {
      console.log('Project update successful:', data)
      onSuccess?.(data)
      setHasChanges(false)
    })
    .catch((error) => {
      console.error('Project update failed:', error)
      setErrors({
        submit: error.response?.data?.detail || t('tms.updateProjectFailed')
      })
    })
  }

  const handleSetAsDefault = () => {
    setDefaultProject(project.project_id)
    .then(() => {
      onSuccess?.()
    })
    .catch((error) => {
      setErrors({
        submit: error.response?.data?.detail || t('tms.setDefaultFailed')
      })
    })
  }

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'testing':
        return <Loader className="w-4 h-4 animate-spin text-blue-500" />
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-500" />
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />
      default:
        return null
    }
  }

  if (!isOpen || !project) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-2">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {t('tms.editProject')}
            </h2>
            {isDefault && (
              <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300 text-xs rounded flex items-center">
                <Star className="w-3 h-3 mr-1 fill-current" />
                {t('tms.default')}
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="p-1"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* General Error */}
          {errors.submit && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
              <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
            </div>
          )}

          {/* Project ID (Read-only) */}
          <Input
            label={t('tms.projectId')}
            value={project.project_id}
            disabled
            className="bg-gray-50 dark:bg-gray-700"
          />

          {/* Project Name */}
          <Input
            label={t('tms.projectName')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder={t('tms.projectNamePlaceholder')}
            error={errors.name}
            required
          />

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tms.apiKey')} *
            </label>
            <div className="relative">
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={formData.api_key}
                onChange={(e) => handleInputChange('api_key', e.target.value)}
                placeholder={t('tms.apiKeyPlaceholder')}
                error={errors.api_key}
                required
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Connection Test */}
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestConnection}
              disabled={!formData.api_key || isTestingConnection}
              className="flex items-center"
            >
              {getConnectionStatusIcon()}
              <span className="ml-2">{t('tms.testConnection')}</span>
            </Button>

            {connectionStatus === 'success' && (
              <span className="text-sm text-green-600 dark:text-green-400">
                {t('tms.connectionSuccessful')}
              </span>
            )}
          </div>

          {errors.connection && (
            <div className="text-sm text-red-600 dark:text-red-400">
              {errors.connection}
            </div>
          )}

          {/* Default Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('tms.defaultTags')}
            </label>

            {/* Existing Tags */}
            {formData.default_tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.default_tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 text-sm rounded"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      className="ml-1 text-blue-500 hover:text-blue-700"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Add Tag Input */}
            <div className="flex space-x-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder={t('tms.addTagPlaceholder')}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Default Settings */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('tms.defaultImportSettings')}
            </h4>

            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.default_overwrite_translations}
                  onChange={(e) => handleInputChange('default_overwrite_translations', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.overwriteExistingTranslations')}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.default_flag_for_review}
                  onChange={(e) => handleInputChange('default_flag_for_review', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.flagImportedStringsForReview')}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.create_missing_locales}
                  onChange={(e) => handleInputChange('create_missing_locales', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.createMissingLocalesAutomatically')}
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => handleInputChange('active', e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  {t('tms.projectActive')}
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4">
            <div>
              {!isDefault && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleSetAsDefault}
                  disabled={isSettingDefault}
                  loading={isSettingDefault}
                  className="flex items-center"
                >
                  <Star className="w-4 h-4 mr-2" />
                  {t('tms.setAsDefault')}
                </Button>
              )}
            </div>

            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isUpdatingProject}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={isUpdatingProject}
                disabled={isUpdatingProject || !hasChanges}
                className="flex items-center"
              >
                <Save className="w-4 h-4 mr-2" />
                {t('common.save')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ProjectEditor